<template>
  <el-dialog
    v-model="showDialog"
    width="420px"
    append-to-body
    z-index="9999"
    style="border-radius: 10px;"
    :lock-scroll="false"
  >
    <template #title>
      <span style="font-size: 24px; font-weight: bold;">Redeem Code</span>
    </template>
    <div class="redeem-code-wrapper">
      <div class="header text-center">
        <div class="subtitle">
          Enter the 16 character code provided by your instructor, bookstore or third party store.
        </div>
      </div>

      <div class="content">
        <div class="input-group">
          <label class="label">Code</label>
          <el-input
            v-model="redeemCode"
            placeholder="Enter your redeem code"
            class="redeem-input"
            :class="{ 'error': showError }"
            @keyup.enter="handleRedeem"
          />
          <div v-if="showError" class="error-message">
            <el-icon><WarningFilled /></el-icon>
            {{ errorMessage }}
          </div>
        </div>

        <div class="button-group">
          <el-button @click="closeDialog" class="cancel-btn">Cancel</el-button>
          <el-button
            type="primary"
            @click="handleRedeem"
            :loading="isLoading"
            class="redeem-btn"
          >
            Redeem
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { bindRedeemCode } from '@/api/user'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits([
  'update:modelValue',
  'refresh-home-data',
])

const showDialog = computed({
  get() {
    return props.modelValue
  },
  set(val) {
    emits('update:modelValue', val)
  },
})

const redeemCode = ref('')
const isLoading = ref(false)
const showError = ref(false)
const errorMessage = ref('')

const resetForm = () => {
  redeemCode.value = ''
  showError.value = false
  errorMessage.value = ''
  isLoading.value = false
}

const closeDialog = () => {
  showDialog.value = false
  resetForm()
}

const handleRedeem = async () => {
  if (!redeemCode.value.trim()) {
    showError.value = true
    errorMessage.value = 'Please enter a redeem code'
    return
  }

  if (redeemCode.value.trim().length !== 16) {
    showError.value = true
    errorMessage.value = 'Code must be 16 characters without spaces.'
    return
  }

  isLoading.value = true
  showError.value = false

  try {
    const response = await bindRedeemCode(redeemCode.value.trim())

    if (response.code === 200) {
      ElMessage.success('Redeem code applied successfully!')
      emits('refresh-home-data')
      closeDialog()
    } else {
      throw new Error(response.msg || 'Failed to redeem code')
    }
  } catch (error) {
    showError.value = true
    errorMessage.value = error.message || 'Failed to redeem code. Please try again.'
  } finally {
    isLoading.value = false
  }
}

watch(showDialog, (newVal) => {
  if (!newVal) {
    resetForm()
  }
})
</script>

<style scoped lang="scss">
.redeem-code-wrapper {
  margin: 0 auto;
}

.header {
  margin-bottom: 20px;

  .subtitle {
    text-align: left;
    font-size: 14px;
    color: #666;
    line-height: 1.5;
  }
}

.content {
  .input-group {
    margin-bottom: 30px;

    .label {
      display: block;
      font-size: 15px;
      font-weight: 500;
      color: #3D3D3D;
      margin-bottom: 8px;
    }

    .redeem-input {
      width: 100%;

      &.error {
        :deep(.el-input__wrapper) {
          border-color: #f56c6c;
        }
      }
    }

    .error-message {
      color: #f56c6c;
      font-size: 12px;
      margin-top: 8px;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .button-group {
    display: flex;
    gap: 16px;
    justify-content: flex-end;

    .cancel-btn {
      border-color: #dcdfe6;
      color: #606266;
    }

    .redeem-btn {
      background-color: #4F6999;
      border-color: #4F6999;
      color: #ffffff;

      &:hover {
        background-color: #3d537a;
        border-color: #3d537a;
      }
    }
  }
}
</style>
